import React, { useEffect, useState } from 'react';
import { API } from '../../api/client';

interface Question {
  id: number;
  title: string;
  prompt: string;
  legend?: string;
}

interface EditState {
  title: string;
  prompt: string;
  legend: string;
}

const EMPTY_EDIT: EditState = { title: '', prompt: '', legend: '' };

export default function AdminEvaluatorQuestions() {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [error, setError] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [newQ, setNewQ] = useState<EditState>({ ...EMPTY_EDIT });

  // Cohort year state.
  const [year, setYear] = useState<number | null>(null);
  const [activeYear, setActiveYear] = useState<number | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [creatingYear, setCreatingYear] = useState(false);
  const [newYearValue, setNewYearValue] = useState<number>(0);
  const [yearMsg, setYearMsg] = useState('');
  const [yearErr, setYearErr] = useState('');

  const load = (y?: number) => {
    setLoading(true);
    setEditingId(null);
    setEditState(null);
    setAddingNew(false);
    API.adminEvaluatorQuestions(y).then(data => {
      if (data) {
        setQuestions(data.questions ?? []);
        setYear(data.year ?? null);
        setActiveYear(data.activeYear ?? null);
        setAvailableYears(data.availableYears ?? []);
      }
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const flashYear = (msg: string, isErr = false) => {
    if (isErr) { setYearErr(msg); setTimeout(() => setYearErr(''), 4000); }
    else { setYearMsg(msg); setTimeout(() => setYearMsg(''), 3000); }
  };

  const onSelectYear = (y: number) => { if (y !== year) load(y); };

  const startCreateYear = () => {
    const base = availableYears.length ? Math.max(...availableYears) : (activeYear ?? new Date().getFullYear());
    setNewYearValue(base + 1);
    setYearErr('');
    setCreatingYear(true);
  };

  const createYear = async () => {
    try {
      await API.seedEvaluatorQuestions(newYearValue);
      setCreatingYear(false);
      flashYear(`Created the ${newYearValue} question set from the previous year. Edit it below, then set it active when ready.`);
      load(newYearValue);
    } catch {
      flashYear(`Could not create ${newYearValue} — questions for that year may already exist.`, true);
    }
  };

  const startEdit = (q: Question) => {
    setEditingId(q.id);
    setEditState({ title: q.title, prompt: q.prompt, legend: q.legend ?? '' });
    setAddingNew(false);
  };

  const cancelEdit = () => { setEditingId(null); setEditState(null); };

  const applyEdit = () => {
    if (!editState || editingId === null) return;
    setQuestions(prev => prev.map(q => q.id !== editingId ? q : {
      ...q,
      title: editState.title,
      prompt: editState.prompt,
      legend: editState.legend || undefined,
    }));
    setEditingId(null);
    setEditState(null);
  };

  const deleteQuestion = (id: number) => {
    setQuestions(prev => prev.filter(q => q.id !== id));
  };

  const moveQuestion = (id: number, dir: -1 | 1) => {
    setQuestions(prev => {
      const qs = [...prev];
      const i = qs.findIndex(q => q.id === id);
      const j = i + dir;
      if (j < 0 || j >= qs.length) return prev;
      [qs[i], qs[j]] = [qs[j], qs[i]];
      return qs;
    });
  };

  const commitNewQuestion = () => {
    if (!newQ.title.trim() || !newQ.prompt.trim()) return;
    const maxId = questions.reduce((m, q) => Math.max(m, q.id), 0);
    setQuestions(prev => [...prev, {
      id: maxId + 1,
      title: newQ.title,
      prompt: newQ.prompt,
      legend: newQ.legend || undefined,
    }]);
    setNewQ({ ...EMPTY_EDIT });
    setAddingNew(false);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await API.updateEvaluatorQuestions({ questions }, year ?? undefined);
      setSavedMsg('Scoring questions saved.');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="phil-loading">Loading…</div>;

  return (
    <>
      <div className="phil-page-heading">Evaluator Questions</div>
      <div className="phil-page-sub">Edit the scoring questions evaluators (FIs) answer for each applicant. Edits to the active year apply to evaluators immediately after saving.</div>

      {/* Cohort year bar */}
      <div className="aq-yearbar">
        <div className="aq-yearbar-group">
          <label className="aq-yearbar-label">Cohort year</label>
          <select
            className="phil-select"
            style={{ width: 'auto', minWidth: 90 }}
            value={year ?? ''}
            onChange={e => onSelectYear(Number(e.target.value))}
          >
            {year != null && !availableYears.includes(year) && <option value={year}>{year}</option>}
            {availableYears.map(y => (
              <option key={y} value={y}>{y}{y === activeYear ? ' • active' : ''}</option>
            ))}
          </select>
          {year === activeYear ? (
            <span className="aq-active-pill">● Evaluators see this year</span>
          ) : (
            <span style={{ fontSize: 12, color: '#888' }}>
              Not the active year — set it in Admin Settings
            </span>
          )}
        </div>

        <div className="aq-yearbar-group">
          {yearMsg && <span style={{ fontSize: 12, color: '#28C840', fontWeight: 600 }}>{yearMsg}</span>}
          {yearErr && <span style={{ fontSize: 12, color: '#E24B4A' }}>{yearErr}</span>}
          {!creatingYear ? (
            <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={startCreateYear}>
              + New cohort year
            </button>
          ) : (
            <span className="aq-create-year">
              <input
                type="number"
                className="phil-input"
                style={{ width: 90 }}
                value={newYearValue}
                onChange={e => setNewYearValue(Number(e.target.value))}
              />
              <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={createYear}>Create</button>
              <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={() => setCreatingYear(false)}>Cancel</button>
              <span className="aq-create-hint">Copies the previous year's questions as a starting point.</span>
            </span>
          )}
        </div>
      </div>

      <div className="phil-card" style={{ marginTop: 16 }}>
        <div className="phil-card-head">
          <h2>Scoring Questions</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {savedMsg && <span style={{ fontSize: 12, color: '#28C840', fontWeight: 600 }}>{savedMsg}</span>}
            {error && <span style={{ fontSize: 12, color: '#E24B4A' }}>{error}</span>}
            <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save Questions'}
            </button>
          </div>
        </div>

        <table className="phil-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>#</th>
              <th style={{ width: 180 }}>Title</th>
              <th>Prompt</th>
              <th>Score legend</th>
              <th style={{ width: 100 }}></th>
            </tr>
          </thead>
          <tbody>
            {questions.map((q, idx) => (
              editingId === q.id && editState ? (
                <tr key={q.id} style={{ background: '#f0f7ff' }}>
                  <td style={{ color: '#aaa', fontSize: 12 }}>{idx + 1}</td>
                  <td>
                    <input
                      className="phil-input"
                      value={editState.title}
                      onChange={e => setEditState({ ...editState, title: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="phil-input"
                      value={editState.prompt}
                      onChange={e => setEditState({ ...editState, prompt: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="phil-input"
                      placeholder="e.g. 1=Disagree · 5=Agree"
                      value={editState.legend}
                      onChange={e => setEditState({ ...editState, legend: e.target.value })}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={applyEdit}>Apply</button>
                      <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={cancelEdit}>Cancel</button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={q.id}>
                  <td style={{ color: '#aaa', fontSize: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <button
                        className="phil-btn phil-btn-secondary phil-btn-sm"
                        style={{ padding: '0 5px', fontSize: 11, lineHeight: '16px', minWidth: 0, visibility: idx === 0 ? 'hidden' : 'visible' }}
                        onClick={() => moveQuestion(q.id, -1)}
                        title="Move up"
                      >▲</button>
                      <span>{idx + 1}</span>
                      <button
                        className="phil-btn phil-btn-secondary phil-btn-sm"
                        style={{ padding: '0 5px', fontSize: 11, lineHeight: '16px', minWidth: 0, visibility: idx === questions.length - 1 ? 'hidden' : 'visible' }}
                        onClick={() => moveQuestion(q.id, 1)}
                        title="Move down"
                      >▼</button>
                    </div>
                  </td>
                  <td><div style={{ fontWeight: 600, fontSize: 13 }}>{q.title}</div></td>
                  <td style={{ fontSize: 13 }}>{q.prompt}</td>
                  <td style={{ fontSize: 12, color: '#555' }}>
                    {q.legend || <span style={{ color: '#ccc' }}>—</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={() => startEdit(q)}>Edit</button>
                      <button
                        className="phil-btn phil-btn-secondary phil-btn-sm"
                        style={{ color: '#E24B4A' }}
                        onClick={() => deleteQuestion(q.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              )
            ))}

            {/* Add new question row */}
            {addingNew && (
              <tr style={{ background: '#f7fbfe' }}>
                <td style={{ color: '#aaa', fontSize: 12 }}>New</td>
                <td>
                  <input
                    className="phil-input"
                    placeholder="Title"
                    value={newQ.title}
                    onChange={e => setNewQ({ ...newQ, title: e.target.value })}
                    autoFocus
                  />
                </td>
                <td>
                  <input
                    className="phil-input"
                    placeholder="Question prompt"
                    value={newQ.prompt}
                    onChange={e => setNewQ({ ...newQ, prompt: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    className="phil-input"
                    placeholder="e.g. 1=Disagree · 5=Agree"
                    value={newQ.legend}
                    onChange={e => setNewQ({ ...newQ, legend: e.target.value })}
                  />
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={commitNewQuestion}>Add</button>
                    <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={() => setAddingNew(false)}>Cancel</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {!addingNew && (
          <div style={{ padding: '12px 18px', borderTop: '1px solid #f0f0f0' }}>
            <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={() => { setAddingNew(true); cancelEdit(); }}>
              + Add Question
            </button>
          </div>
        )}
      </div>
    </>
  );
}
